const SELECTOR = '[data-df-launch-text-sync]';
const initializedElements = new WeakSet();

function clamp(value, min = 0, max = 1) {
  return Math.min(max, Math.max(min, value));
}

function smoothstep(value) {
  const progress = clamp(value);
  return progress * progress * (3 - 2 * progress);
}

function progressBetween(time, start, end) {
  if (end <= start) {
    return time >= end ? 1 : 0;
  }

  return smoothstep((time - start) / (end - start));
}

function initializeLaunchHeading(element) {
  if (initializedElements.has(element)) return;

  const slide = element.closest('[data-slide-type="video"]');
  const video = slide?.querySelector('video');
  const content = element.querySelector('.df-launch-hero__content');

  if (!video || !content) return;

  initializedElements.add(element);

  const timing = {
    fadeInStart: Number(element.dataset.dfLaunchFadeInStart),
    fadeInEnd: Number(element.dataset.dfLaunchFadeInEnd),
    fadeOutStart: Number(element.dataset.dfLaunchFadeOutStart),
    fadeOutEnd: Number(element.dataset.dfLaunchFadeOutEnd),
  };

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  let animationFrame;
  let autoplayAttempt;

  async function tryAutoplay() {
    if (!video.paused && !video.ended) {
      element.removeAttribute('data-df-autoplay-blocked');
      return;
    }

    if (autoplayAttempt) return autoplayAttempt;

    video.autoplay = true;
    video.muted = true;
    video.defaultMuted = true;
    video.playsInline = true;
    video.setAttribute('autoplay', '');
    video.setAttribute('muted', '');
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');

    autoplayAttempt = video.play()
      .then(() => {
        element.removeAttribute('data-df-autoplay-blocked');
      })
      .catch((error) => {
        if (error?.name === 'NotAllowedError') {
          element.dataset.dfAutoplayBlocked = 'true';
        }
      })
      .finally(() => {
        autoplayAttempt = undefined;
      });

    return autoplayAttempt;
  }

  function render() {
    const time = video.currentTime || 0;
    const fadeIn = progressBetween(time, timing.fadeInStart, timing.fadeInEnd);
    const fadeOut = progressBetween(time, timing.fadeOutStart, timing.fadeOutEnd);
    const opacity = clamp(fadeIn * (1 - fadeOut));

    const translateY = reduceMotion.matches
      ? 0
      : 16 * (1 - fadeIn) - 6 * fadeOut;
    const blur = reduceMotion.matches
      ? 0
      : 11 * (1 - fadeIn) + 8 * fadeOut;
    const letterSpacing = reduceMotion.matches
      ? -0.015
      : -0.015 + 0.05 * (1 - fadeIn);

    content.style.setProperty('--df-launch-opacity', opacity.toFixed(4));
    content.style.setProperty('--df-launch-translate-y', `${translateY.toFixed(2)}px`);
    content.style.setProperty('--df-launch-blur', `${blur.toFixed(2)}px`);
    content.style.setProperty('--df-launch-letter-spacing', `${letterSpacing.toFixed(4)}em`);
  }

  function tick() {
    render();
    animationFrame = video.paused ? undefined : requestAnimationFrame(tick);
  }

  function start() {
    if (animationFrame) cancelAnimationFrame(animationFrame);
    tick();
  }

  function stop() {
    if (animationFrame) cancelAnimationFrame(animationFrame);
    animationFrame = undefined;
    render();
  }

  video.addEventListener('play', () => {
    element.removeAttribute('data-df-autoplay-blocked');
    start();
  });
  video.addEventListener('pause', stop);
  video.addEventListener('ended', stop);
  video.addEventListener('seeking', render);
  video.addEventListener('seeked', render);
  video.addEventListener('timeupdate', render);
  video.addEventListener('loadedmetadata', tryAutoplay, { once: true });
  video.addEventListener('canplay', tryAutoplay, { once: true });
  reduceMotion.addEventListener?.('change', render);

  const visibilityObserver = new IntersectionObserver((entries) => {
    const isVisible = entries.some((entry) => entry.isIntersecting);

    if (isVisible) {
      tryAutoplay();
    } else {
      video.pause();
      stop();
    }
  }, { threshold: 0.1 });

  visibilityObserver.observe(slide);

  window.addEventListener('pageshow', tryAutoplay);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') tryAutoplay();
  });

  render();
  if (!video.paused) {
    start();
  } else {
    tryAutoplay();
  }
}

function initializeWithin(root = document) {
  if (root.matches?.(SELECTOR)) initializeLaunchHeading(root);
  root.querySelectorAll?.(SELECTOR).forEach(initializeLaunchHeading);
}

initializeWithin();

document.addEventListener('shopify:section:load', (event) => {
  initializeWithin(event.target);
});

document.addEventListener('shopify:block:select', (event) => {
  initializeWithin(event.target);
});
